import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import {
  BaseOneResponseInterface,
  BulkResponseDataInterface,
} from '../../../../shared/model/interface/crud-response-interface.model';
import * as oeeAppReducer from '../../../oee.reducer';
import * as AppActions from '../../../app/actions';
import { ErrorMessageService } from '../../../../shared/service/error-message.service';
import { IWorkOrderSetting } from '../../../../view/settings/api-automation-settings/work-order-settings/work-order-settings.model';
import { WorkOrderSettingService } from './work-order-settings.service';
import {
  ActionTypes,
  WorkOrderSettingsLoading,
  WorkOrderSettingsLoaded,
  FetchError,
  GetWorkOrderSetting,
  GetWorkOrderSettingCompleted,
  EditWorkOrderSetting,
  EditWorkOrderSettingCompleted,
  AddWorkOrderSetting,
  AddWorkOrderSettingCompleted,
  DeleteWorkOrderSettings,
  DeleteWorkOrderSettingsCompleted,
  ChangeActivitiesDataLoading,
  ChangeActivitiesDataCompleted,
  HeartbeatSettingsDataLoading,
  HeartbeatSettingsDataCompleted,
  ProductsDataLoading,
  ProductsDataCompleted,
} from './work-order-settings.actions';
import { ApiAutomationLookupService } from '../shared/api-automation-lookup.service';
import { EApiAutomationResource } from '../shared/api-automation.model';
import { ProductService } from '../../../../shared/service/filter/product.service';

@Injectable()
export class WorkOrderSettingEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: WorkOrderSettingService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly errorMessageService: ErrorMessageService,
    private readonly lookupService: ApiAutomationLookupService,
    private readonly productService: ProductService,
  ) {}

  getDataForDatatable = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.WORK_ORDER_SETTINGS_DATA_LOADING),
      switchMap((payload: WorkOrderSettingsLoading) => {
        const andConditions = [];
        const searchText: string = payload.params.searchText?.trim() ?? '';
        let httpParams: HttpParams = new HttpParams()
          .append('sort', 'id,DESC')
          .append('limit', String(payload.params.perPage || 1000))
          .append('page', String(payload.params.page));

        if (searchText) {
          andConditions.push({
            $or: [
              { namePre: { $cont: searchText } },
              { namePost: { $cont: searchText } },
              { 'heartbeatSetting.name': { $cont: searchText } },
              { 'defaultProduct.name': { $cont: searchText } },
            ],
          });
        }

        if (andConditions.length > 0) {
          httpParams = httpParams.set('s', JSON.stringify({ $and: andConditions }));
        }

        if (payload.params.orderBy && payload.params.orderDesc !== undefined) {
          httpParams = httpParams.set('sort', `${payload.params.orderBy},${payload.params.orderDesc}`);
        }

        return this.service.getData(httpParams).pipe(
          switchMap((response) => of(new WorkOrderSettingsLoaded(response))),
          catchError((error) => of(new FetchError(error))),
        );
      }),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.GET_WORK_ORDER_SETTING),
      switchMap((objectData: GetWorkOrderSetting) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOne(objectData.recordId).pipe(
          switchMap((response: BaseOneResponseInterface<IWorkOrderSetting>) =>
            of(new GetWorkOrderSettingCompleted(response), new AppActions.HideLoader()),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  addOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ADD_WORK_ORDER_SETTING),
      switchMap((objectData: AddWorkOrderSetting) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.addOne(objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IWorkOrderSetting>) =>
            of(
              new AddWorkOrderSettingCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  editOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.EDIT_WORK_ORDER_SETTING),
      switchMap((objectData: EditWorkOrderSetting) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.editOne(objectData.recordId, objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IWorkOrderSetting>) =>
            of(
              new EditWorkOrderSettingCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  deleteOnes = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DELETE_WORK_ORDER_SETTINGS),
      switchMap((objectData: DeleteWorkOrderSettings) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.deleteOnes(objectData.payload).pipe(
          switchMap((response: BulkResponseDataInterface) => {
            this.errorMessageService.getTranslatedErrorMessage(response.data);

            return of(
              new DeleteWorkOrderSettingsCompleted(objectData.payload.length > 1, response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  getChangeActivitiesData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.CHANGE_ACTIVITIES_DATA_LOADING),
      switchMap((objectData: ChangeActivitiesDataLoading) =>
        this.lookupService.getOptions(EApiAutomationResource.changeActivity).pipe(
          switchMap((response) => of(new ChangeActivitiesDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getHeartbeatSettingsData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.HEARTBEAT_SETTINGS_DATA_LOADING),
      switchMap(() =>
        this.lookupService.getOptions(EApiAutomationResource.heartbeatSettings).pipe(
          switchMap((response) => of(new HeartbeatSettingsDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getProductsData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.PRODUCTS_DATA_LOADING),
      switchMap(() =>
        from(this.productService.getData(new HttpParams().set('limit', '1000'))).pipe(
          switchMap((response) => of(new ProductsDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );
}
